import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router";
import {
  Select as MuiSelect,
  TextField as MuiTextField,
  InputLabel as MuiInputLabel,
  FormControl as MuiFormControl,
  Autocomplete as MuiAutocomplete,
} from "@mui/material";
import { Search as MuiSearchIcon } from "@mui/icons-material";
import {
  getDepartmentByMatch,
  getCommitmentNameByMatch,
  getCommitmentProjectByMatch,
  getCommitmentAwardByMatch,
  getCommitmentTaskByMatch,
} from "app/services/commitmentsService";
import { useCommitmentContext } from "app/services/commitmentContext";
import { ResetFilter } from "app/shared/ui/FilterPanel/ResetFilter";
import { useAlerts } from "common";

const searchIconStyles = {
  "& .MuiAutocomplete-popupIndicatorOpen": {
    // Stop MUI from rotating the icon when the popup is open
    rotate: "180deg",
  },
};

export const CommitmentsFilter = () => {
  // For fetching up the multilingual text object using i18next.
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { setAlert, clearAlert } = useAlerts();

  const [loading, setLoading] = useState(false);

  // Filter Context
  const { filters, setFilters, pagination, setPagination } =
    useCommitmentContext();
  // State variable and its function for Commitment Name
  const [dealName, setDealName] = useState(filters.dealName);
  // State variable and its function for Department Name
  const [departmentName, setDepartmentName] = useState(filters.departmentName);
  // State variable and its function for Department Code
  const [departmentOrdCode, setDepartmentOrdCode] = useState(
    filters.departmentOrdCode,
  );
  // State variable and its function for Status
  const [status, setStatus] = useState(filters.status);

  // State variable and its function for commitment project
  const [project, setProject] = useState(filters.project);

  // State variable and its function for commitment task
  const [task, setTask] = useState(filters.task);

  // State variable and its function for commitment award
  const [award, setAward] = useState(filters.award);

  // State variable and its function for Commitment Options
  const [commitmentNameOptions, setCommitmentNameOptions] = useState([]);
  // State variable and its function for Department Options
  const [departmentNameOptions, setDepartmentNameOptions] = useState([]);

  // State variable and its function for commitment project Options
  const [projectOptions, setProjectOptions] = useState([]);

  // State variable and its function for commitment task Options
  const [taskOptions, setTaskOptions] = useState([]);

  // State variable and its function for commitment award Options
  const [awardOptions, setAwardOptions] = useState([]);

  const [disableTaskNumber, setDisableTaskNumber] = useState(
    filters.project || filters.task ? false : true,
  );

  // Function to reset the Filter value and re-load Deals data accordingly
  const resetFn = () => {
    setDealName("");
    setDepartmentName("");
    setDepartmentOrdCode("");
    setStatus("");
    setProject("");
    setTask("");
    setAward("");
    setFilters({
      dealName: "",
      departmentName: "",
      departmentOrdCode: "",
      status: "",
      project: "",
      task: "",
      award: "",
    });
    setCommitmentNameOptions([]);
    setDepartmentNameOptions([]);
    setProjectOptions([]);
    setTaskOptions([]);
    setAwardOptions([]);
    setDisableTaskNumber(true);
  };

  // useEffect to act on the change of Filter Data variables.
  useEffect(() => {
    const params = { dealName, departmentName, status, project, task, award };
    const queryParams = Object.keys(params)
      .filter((key) => params[key])
      .map((key) => {
        return `${key}=${encodeURIComponent(params[key])}`;
      })
      .join("&");
    navigate(`${location.pathname}?${queryParams}`);
  }, [
    dealName,
    departmentName,
    departmentOrdCode,
    status,
    project,
    task,
    award,
    navigate,
    location.pathname,
  ]);

  /* Commitments Filter function to show MuiAutocomplete 
    inputs and filter data accordingly. */
  return (
    <>
      <MuiFormControl variant="outlined">
        <MuiAutocomplete
          size="small"
          id="commitmentNameFilter"
          options={commitmentNameOptions || []}
          getOptionLabel={(option) => option}
          value={dealName}
          blurOnSelect
          clearOnEscape
          loading={loading}
          popupIcon={<MuiSearchIcon />}
          sx={searchIconStyles}
          /**
           * On change event for MuiAutocomplete
           *
           * 1. Setting up the values for Filter via useState variable.
           * */
          onChange={(e, value) => {
            const dealName = value;
            setDealName(dealName);
            setFilters({
              ...filters,
              dealName,
            });
            setPagination({
              ...pagination,
              page: 0,
            });
          }}
          onInputChange={(e, inputValue) => {
            if (!inputValue || inputValue.trim() === "") {
              setCommitmentNameOptions([]);
              return;
            }
            return getCommitmentNameByMatch(
              inputValue,
              setCommitmentNameOptions,
              setLoading,
              setAlert,
              clearAlert,
            );
          }}
          renderInput={(params) => (
            <MuiTextField
              {...params}
              label={t(
                "Commitments.filterPanelItems.options.commitmentName.label",
              )}
            />
          )}
        />
      </MuiFormControl>
      <MuiFormControl variant="outlined">
        <MuiAutocomplete
          size="small"
          id="departmentNameFilter"
          options={departmentNameOptions || []}
          getOptionLabel={(option) => option || ""}
          value={departmentName}
          clearOnEscape
          blurOnSelect
          loading={loading}
          popupIcon={<MuiSearchIcon />}
          sx={searchIconStyles}
          /**
           * On change event for MuiAutocomplete
           *
           * 1. Setting up the values for Filter via useState variable.
           * */
          onChange={(e, value) => {
            const departmentName = value;
            const departmentOrdCode = value
              ? value.split("(").pop().split(")")[0].trim()
              : "";
            setDepartmentName(departmentName);
            setDepartmentOrdCode(departmentOrdCode);
            setFilters({
              ...filters,
              departmentName,
              departmentOrdCode,
            });
            setPagination({
              ...pagination,
              page: 0,
            });
          }}
          onInputChange={(e, inputValue) => {
            if (!inputValue || inputValue.trim() === "") {
              setDepartmentNameOptions([]);
              return;
            }
            return getDepartmentByMatch(
              inputValue,
              setDepartmentNameOptions,
              setLoading,
              setAlert,
              clearAlert,
            );
          }}
          renderInput={(params) => (
            <MuiTextField
              {...params}
              label={t(
                "Commitments.filterPanelItems.options.departmentName.label",
              )}
            />
          )}
        />
      </MuiFormControl>
      <MuiFormControl variant="outlined" size="small">
        <MuiInputLabel id="statusLabel">
          {t("Commitments.filterPanelItems.options.status.label")}
        </MuiInputLabel>
        <MuiSelect
          native
          labelId="statusSelectLabel"
          label={t("Commitments.filterPanelItems.options.status.label")}
          id="statusFilter"
          value={status || ""}
          onChange={(e, value) => {
            const status = e.target.value;
            setStatus(status);
            document.getElementById("statusFilter").blur();
            setFilters({
              ...filters,
              status,
            });
            setPagination({
              ...pagination,
              page: 0,
            });
          }}
        >
          <option aria-label="" value="" />
          <option value={0}>
            {t(
              "Commitments.filterPanelItems.options.statusDropdownValues.statusActiveDropdownValue",
            )}
          </option>
          <option value={1}>
            {t(
              "Commitments.filterPanelItems.options.statusDropdownValues.statusInactiveDropdownValue",
            )}
          </option>
        </MuiSelect>
      </MuiFormControl>
      <MuiFormControl variant="outlined">
        <MuiAutocomplete
          size="small"
          id="commitmentProjectFilters"
          options={projectOptions || []}
          getOptionLabel={(option) => option}
          value={project}
          clearOnEscape
          blurOnSelect
          loading={loading}
          popupIcon={<MuiSearchIcon />}
          sx={searchIconStyles}
          /**
           * On change event for MuiAutocomplete
           *
           * 1. Setting up the values for Filter via useState variable.
           * 2. Loading the Task list of corresponding Project Number.
           * 3. Enabling the Task Number list after calling up the
           * function for loading up the data for Task list.
           * */
          onChange={(e, value) => {
            const project = value;
            setProject(value);
            setDisableTaskNumber(false);
            setFilters({
              ...filters,
              project,
            });
            setPagination({
              ...pagination,
              page: 0,
            });
          }}
          onInputChange={(e, inputValue) => {
            if (!inputValue || inputValue.trim() === "") {
              setProjectOptions([]);
              setTask("");
              setDisableTaskNumber(true);
              setTaskOptions([]);
              return;
            }
            return getCommitmentProjectByMatch(
              inputValue,
              setProjectOptions,
              setLoading,
              setAlert,
              clearAlert,
            );
          }}
          renderInput={(params) => (
            <MuiTextField
              {...params}
              label={t("PTAs.filterPanelItems.options.project.label")}
            />
          )}
        />
      </MuiFormControl>
      <MuiFormControl variant="outlined">
        <MuiAutocomplete
          size="small"
          id="commitmentTaskFilters"
          options={taskOptions || []}
          getOptionLabel={(option) => option}
          value={task}
          clearOnEscape
          blurOnSelect
          loading={loading}
          // Disabling/Enabling the Task Number MuiAutocomplete
          disabled={disableTaskNumber}
          popupIcon={<MuiSearchIcon />}
          sx={searchIconStyles}
          /**
           * On change event for MuiAutocomplete
           *
           * 1. Setting up the values for Filter via useState variable.
           * */
          onChange={(e, value) => {
            const task = value;
            setTask(value);
            setFilters({
              ...filters,
              task,
            });
            setPagination({
              ...pagination,
              page: 0,
            });
          }}
          onInputChange={(e, inputValue) => {
            if (!inputValue || inputValue.trim() === "") {
              setTaskOptions([]);
              return;
            }
            return getCommitmentTaskByMatch(
              inputValue,
              setTaskOptions,
              project,
              setLoading,
              setAlert,
              clearAlert,
            );
          }}
          renderInput={(params) => (
            <MuiTextField
              {...params}
              label={t("PTAs.filterPanelItems.options.task.label")}
            />
          )}
        />
      </MuiFormControl>
      <MuiFormControl variant="outlined">
        <MuiAutocomplete
          size="small"
          id="commitmentAwardFilters"
          options={awardOptions || []}
          getOptionLabel={(option) => option}
          value={award}
          clearOnEscape
          loading={loading}
          blurOnSelect
          popupIcon={<MuiSearchIcon />}
          sx={searchIconStyles}
          /**
           * On change event for MuiAutocomplete
           *
           * 1. Setting up the values for Filter via useState variable.
           * */
          onChange={(e, value) => {
            const award = value;
            setAward(value);
            setFilters({
              ...filters,
              award,
            });
            setPagination({
              ...pagination,
              page: 0,
            });
          }}
          onInputChange={(e, inputValue) => {
            if (!inputValue || inputValue.trim() === "") {
              setAwardOptions([]);
              return;
            }
            return getCommitmentAwardByMatch(
              inputValue,
              setAwardOptions,
              setLoading,
              setAlert,
              clearAlert,
            );
          }}
          renderInput={(params) => (
            <MuiTextField
              {...params}
              label={t("PTAs.filterPanelItems.options.award.label")}
            />
          )}
        />
      </MuiFormControl>
      <ResetFilter resetFn={resetFn} />
    </>
  );
};
